import React, { useEffect, useCallback } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resendSignUpCode,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Capacitor, registerPlugin } from '@capacitor/core';
import toast from 'react-hot-toast';
import useAuthStore from '../stores/useAuthStore';

const AppleSignIn = registerPlugin('AppleSignIn');

async function checkAuth() {
  const store = useAuthStore.getState();
  try {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    if (currentUser && session.tokens) {
      let attrs = {};
      try {
        const attrList = await fetchUserAttributes();
        if (Array.isArray(attrList)) {
          attrList.forEach((a) => { attrs[a.Name || a.name] = a.Value || a.value; });
        } else if (attrList && typeof attrList === 'object') {
          attrs = attrList;
        }
      } catch {}
      store.setUser({ ...currentUser, attributes: attrs });
      store.closeAuthModal();
    }
  } catch {
    store.clearAuth();
  } finally {
    store.setLoading(false);
  }
}

export function AuthProvider({ children }) {
  // Mount-time session check + Hub listener
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const listener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        checkAuth();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        const message = 'Sign in failed. Please try again.';
        useAuthStore.getState().setError(message);
        toast.error(message);
        useAuthStore.getState().setLoading(false);
      }
    });
    return () => listener();
  }, []);

  useEffect(() => {
    const handler = () => {
      const store = useAuthStore.getState();
      store.clearAuth();
      store.openAuthModal();
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, []);

  return <>{children}</>;
}

// Stable hook: returns the same shape consumers expect from the old context.
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const authState = useAuthStore((s) => s.authState);
  const pendingEmail = useAuthStore((s) => s.pendingEmail);
  const error = useAuthStore((s) => s.error);
  const showAuthModal = useAuthStore((s) => s.showAuthModal);

  const setError = useAuthStore((s) => s.setError);
  const setPendingEmail = useAuthStore((s) => s.setPendingEmail);
  const setAuthState = useAuthStore((s) => s.setAuthState);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const openAuthModal = useAuthStore((s) => s.openAuthModal);
  const closeAuthModal = useAuthStore((s) => s.closeAuthModal);

  const handleSignIn = useCallback(async (email, password) => {
    setError('');
    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        await checkAuth();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setPendingEmail(email);
        setAuthState('confirmSignUp');
      }
    } catch (err) {
      setError(err.message || 'Sign in failed');
      throw err;
    }
  }, [setError, setPendingEmail, setAuthState]);

  const handleSignUp = useCallback(async (email, password) => {
    setError('');
    try {
      const result = await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        setPendingEmail(email);
        setAuthState('confirmSignUp');
      } else if (result.isSignUpComplete || result.nextStep?.signUpStep === 'DONE') {
        // Safety net: this pool actually sends a {####} email code (there is no
        // PreSignUp auto-confirm trigger), so the live path is CONFIRM_SIGN_UP
        // above. Keep this branch for pool-config changes so a DONE signup is
        // never silently stranded on the form.
        const signInResult = await signIn({ username: email, password });
        if (signInResult.isSignedIn) await checkAuth();
      }
    } catch (err) {
      setError(err.message || 'Sign up failed');
      throw err;
    }
  }, [setError, setPendingEmail, setAuthState]);

  const handleConfirmSignUp = useCallback(async (code, password) => {
    setError('');
    try {
      await confirmSignUp({ username: pendingEmail, confirmationCode: code });
      // Auto sign-in after confirmation when the caller still has the password
      // (both auth surfaces keep it in local state). Fall back to the sign-in
      // form only if that fails.
      if (password) {
        try {
          const signInResult = await signIn({ username: pendingEmail, password });
          if (signInResult.isSignedIn) {
            await checkAuth();
            return;
          }
        } catch (err) {
          console.warn('Post-confirmation sign-in failed:', err?.message);
        }
      }
      // The account is verified but we could NOT sign the user in (password
      // lost when the modal closed, or sign-in failed). Say so explicitly — a
      // silent drop to the sign-in form stranded real users here: "verified"
      // read as "done", they left, and never came back.
      toast.success('Email verified! Now sign in with your password.', { duration: 6000 });
      setAuthState('signIn');
    } catch (err) {
      setError(err.message || 'Confirmation failed');
      throw err;
    }
  }, [pendingEmail, setError, setAuthState]);

  const handleResendCode = useCallback(async () => {
    setError('');
    try {
      await resendSignUpCode({ username: pendingEmail });
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    }
  }, [pendingEmail, setError]);

  const handleAppleSignIn = useCallback(async () => {
    setError('');
    const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    try {
      if (isNativeIOS) {
        const result = await AppleSignIn.signIn();
        const { identityToken, fullName } = result;

        const API_ENDPOINT = 'https://ctqysft3ek.execute-api.eu-west-2.amazonaws.com/tasks';
        const response = await fetch(`${API_ENDPOINT}/auth/apple-native`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identityToken, fullName }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Apple sign in failed (${response.status})`);
        }
        const { appleEmail, password } = await response.json();
        try { await signOut(); } catch {}
        const signInResult = await signIn({ username: appleEmail, password });
        if (signInResult.isSignedIn) await checkAuth();
      } else {
        await signInWithRedirect({ provider: { custom: 'SignInWithApple' } });
      }
    } catch (err) {
      const message = err.message === 'Sign in was cancelled'
        ? 'Sign in cancelled'
        : (err.message || 'Apple Sign In failed');
      setError(message);
      toast.error(message);
    }
  }, [setError]);

  const handleSignOut = useCallback(async () => {
    try { await signOut(); } catch {}
    clearAuth();
    setError('');
  }, [clearAuth, setError]);

  const switchToSignUp = useCallback(() => {
    setError('');
    setAuthState('signUp');
  }, [setError, setAuthState]);

  const switchToSignIn = useCallback(() => {
    setError('');
    setAuthState('signIn');
  }, [setError, setAuthState]);

  return {
    user,
    isAuthenticated,
    isLoading,
    authState,
    pendingEmail,
    error,
    showAuthModal,
    handleSignIn,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    handleAppleSignIn,
    handleSignOut,
    openAuthModal,
    closeAuthModal,
    switchToSignUp,
    switchToSignIn,
  };
}
